'use client'

import Image from 'next/image'
import { Typography } from '@/shared/ui/atoms'
import { CustomButton } from '@/shared/ui/molecules'
import { useEffect, useRef, useState } from 'react'
import { useTranslations } from 'next-intl'

interface HospitalIntroductionProps {
  introduction: string
  /** 새 상세 페이지에서만 쓰는 소개 썸네일(기존 요약 카드가 들고 있던 이미지) */
  thumbnailUrl?: string
  /**
   * 새 상세 페이지의 섹션 셸 안에 들어갈 때. 제목과 바깥 여백은 섹션이 갖고 있으므로
   * 여기서는 내용만 그린다. 기본값(false)은 기존 상세 페이지의 모습 그대로다.
   */
  embedded?: boolean
}

const HospitalIntroduction = ({ introduction, thumbnailUrl, embedded = false }: HospitalIntroductionProps) => {
  const t = useTranslations()
  const [isMore, setIsMore] = useState(false)
  const [isShowMoreButton, setIsShowMoreButton] = useState(false)
  const textRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    const el = textRef.current
    if (!el) return

    const check = () => {
      setIsShowMoreButton(el.scrollHeight > el.clientHeight + 1)
    }

    const raf = window.requestAnimationFrame(check)
    window.addEventListener("resize", check)

    return () => {
      window.cancelAnimationFrame(raf)
      window.removeEventListener("resize", check)
    }
  }, [introduction])

  return (
    <div id={embedded ? undefined : "hospital-introduction"} className={embedded ? "" : "px-4 pb-[30px]"}>
      <Typography
        as={embedded ? "h3" : "p"}
        size={embedded ? 15 : 18}
        className={embedded ? "pb-2 font-bold text-gray-900" : "py-3 text-gray-900 font-bold"}
      >
        {t("partnerHospitalSummary.cards.introduction")}
      </Typography>
      <div className="flex flex-col items-center gap-5">
        <div className="flex w-full gap-3">
          {embedded && thumbnailUrl?.trim() ? (
            <div className="relative h-[72px] w-[72px] flex-shrink-0 overflow-hidden rounded-lg">
              <Image src={thumbnailUrl} alt="" fill sizes="72px" className="object-cover" />
            </div>
          ) : null}
          <Typography
            ref={textRef}
            size={15}
            lineHeight={22}
            className={`flex-1 text-gray-600 text-overflow-ellipsis ${isMore ? "line-clamp-none" : "line-clamp-5"}`}
          >
            {introduction}
          </Typography>
        </div>
        {isShowMoreButton && (
          <CustomButton className="py-2 w-full bg-gray-100 rounded-lg" onClick={() => {setIsMore(!isMore)}}>
            <Typography size={14} lineHeight={20} className="text-gray-600 font-medium">
              {isMore ? t("common.collapse") : t("common.more")}
            </Typography>
          </CustomButton>
        )}
      </div>
    </div>
  )
}

export default HospitalIntroduction